import {
    createContext,
    useEffect,
    useMemo,
    useState,
    ReactNode,
    useContext,
    useCallback,
} from "react";
import { IStore } from "@snackpass/snackpass-types";
import moment, { Moment } from "moment";
import { useSelector } from "react-redux";
import axios from "axios";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { selectCustomHourOffset, setCustomHourOffset } from "src/redux/slices/ordersSettings";

import {
    MenuInsightsItemRow,
    MenuModifier,
} from "#reports/menu-item-insights/types";
import { CustomerTableRow } from "#reports-customers/types";
import { getActiveStore } from "src/redux/selectors";
import { getUserIsSnackpassEmployee } from "src/redux/selectors/user";
import api from "src/api/rest";
import { LocationMenuItemRow } from "#reports/location-menu-categories/types";
import { PromotionsReportRow } from "#reports/promotions/types";
import { GiftCardBalanceDataRow } from "#reports/gift-card-balances/types";
import { GiftCardTransactionDataRow } from "#reports/gift-card-transactions/types";
import { logAndSendError } from "src/utils/errors";
import { TableChannelDataRow } from "#reports/sales-channels/types";
import {
    QueryFilter,
    ComparisonType,
    GranularityType,
    TableSummaryDataRow,
    SummaryTilesData,
    SalesHourlyAggregateReport,
    SalesDailyAggregateReport,
    TableLocationData,
} from "#reports-sales-comparison/types";

export type ReportsContextType = {
    reportsState: ReportsStateType;
    setReportsState: (
        reportsContext:
            | ReportsStateType
            | ((prevState: ReportsStateType) => ReportsStateType),
    ) => void;
    setCustomHourOffset: (offset: string) => void;
};

export const defaultFilter: QueryFilter = {
    source: [],
    channel: [],
    fulfillment: [],
    payment: [],
    storeIds: [],
};

const initialWeekRange = [moment().startOf("isoWeek"), moment().endOf("day")];

export const defaultDateRanges: Moment[][] = [
    initialWeekRange,
    initialWeekRange,
];

export const defaultReportsState: ReportsStateType = {
    allTime: true,
    granularity: GranularityType.DAILY,
    dateRanges: defaultDateRanges,
    comparison: ComparisonType.NONE,
    filter: defaultFilter,
    channels: [],
    stores: [],
    channelsData: undefined,
    menuInsightsData: undefined,
    locationMenuInsightsData: undefined,
    locationReportsData: undefined,
    customerInsightsData: undefined,
    promotionsReportData: undefined,
    giftCardBalanceReportData: undefined,
    giftCardTransactionReportData: undefined,
    customHourOffset: undefined,
};

export const ReportsContext = createContext<ReportsContextType>({
    reportsState: defaultReportsState,
    setReportsState: () => {},
    setCustomHourOffset: () => {},
});

export const ReportsContextProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const dispatch = useAppDispatch();
    const customHourOffset = useAppSelector(selectCustomHourOffset) || "0";

    const handleSetCustomHourOffset = useCallback((offset: string) => {
        dispatch(setCustomHourOffset(offset));
    }, [dispatch]);

    const activeStore = useSelector(getActiveStore);
    const isSnackpassEmployee = useAppSelector(getUserIsSnackpassEmployee);

    const [reportsState, setReportsState] =
        useState<ReportsStateType>({
            ...defaultReportsState,
            customHourOffset,
        });

    // Update reportsState when customHourOffset changes in Redux
    useEffect(() => {
        setReportsState(prev => ({
            ...prev,
            customHourOffset,
        }));
    }, [customHourOffset]);

    const selectedStores = reportsState.filter.storeIds;

    const reportsContextValue = useMemo(
        () => ({ 
            reportsState,
            setReportsState,
            setCustomHourOffset: handleSetCustomHourOffset,
        }),
        [reportsState, setReportsState, handleSetCustomHourOffset],
    );

    const fetchChannels = () => {
        if (!activeStore?._id) {
            return;
        }

        const params = {
            storeId: activeStore?._id,
            ...(selectedStores.length > 0
                ? { storeIds: JSON.stringify(selectedStores) }
                : {}),
        };

        api.reports
            .getSalesChannels(params)
            .then((res) => {
                setReportsState((reportsState) => ({
                    ...reportsState,
                    channels: res.data?.channels,
                }));
            })
            .catch((e) => {
                if (axios.isCancel(e?.cause)) return;
                logAndSendError(e);
            });
    };

    const fetchAdminStores = () => {
        setReportsState((reportsState) => ({ ...reportsState, stores: [] }));
        api.stores
            .getMyAdminStores()
            .then((res) => {
                const stores = res.data?.stores;
                setReportsState((reportsState) => ({
                    ...reportsState,
                    stores,
                     // NOTE: CX accesibility. We're not admins to the active store but we want to see the data
                        filter: {
                        ...reportsState.filter,
                        storeIds: [
                            ...(isSnackpassEmployee &&
                            activeStore?._id &&
                            !reportsState.filter.storeIds.includes(
                                activeStore._id,
                            )
                                ? [activeStore._id]
                                : []),
                            ...reportsState.filter.storeIds,
                        ],
                    },
                }));
            })
            .catch((e) => {
                logAndSendError(e);
            });
    };

    useEffect(() => {
        fetchChannels();
        fetchAdminStores();
    }, [activeStore?._id]);

    useEffect(() => {
        fetchChannels();
    }, [selectedStores]);

    useDeepCompareEffect(() => {
        setReportsState({
            ...reportsState,
            channelsData: undefined,
            menuInsightsData: undefined,
            locationMenuInsightsData: undefined,
            locationReportsData: undefined,
            customerInsightsData: undefined,
            promotionsReportData: undefined,
            giftCardBalanceReportData: undefined,
            giftCardTransactionReportData: undefined,
        });
    }, [reportsState.filter, reportsState.dateRanges, reportsState.allTime]);

    return (
        <ReportsContext.Provider value={reportsContextValue}>
            {children}
        </ReportsContext.Provider>
    );
};

export function useReports() {
    const context = useContext(ReportsContext);
    if (!context) {
        throw new Error(
            "useReports must be used within a ReportsContextProvider",
        );
    }
    return context;
}

export type GiftCardBalancesDataType = {
    giftCardBalancesDataLoading?: boolean;
    giftCardBalancesDataFailed?: boolean;
    giftCardBalancesData: GiftCardBalanceDataRow[] | undefined;
};

export type GiftCardTransactionDataType = {
    giftCardTransactionsDataLoading?: boolean;
    giftCardTransactionsDataFailed?: boolean;
    giftCardTransactionsData: GiftCardTransactionDataRow[] | undefined;
};

export type LocationReportsDataType = {
    salesSummaryDataLoading?: boolean;
    salesSummaryDataFailed?: boolean;
    salesSummaryData: SummaryTilesData | undefined;

    salesReportAggregateDataLoading?: boolean;
    salesReportAggregateDataFailed?: boolean;
    salesReportAggregateData: TableSummaryDataRow[] | undefined;

    salesReportDataLoading?: boolean;
    salesReportDataFailed?: boolean;
    salesReportData: { [storeId: string]: TableLocationData } | undefined;
    salesHourlyAggregateReportData:
        | { [storeId: string]: SalesHourlyAggregateReport[] }
        | undefined;
    salesDailyAggregateReportData:
        | { [storeId: string]: SalesDailyAggregateReport[] }
        | undefined;
};

export type MenuInsightsDataType = {
    itemInsightsDataLoading?: boolean;
    itemInsightsData: MenuInsightsItemRow[] | undefined;
    itemInsightsDataFailed?: boolean;
    itemTableModifierData:
        | { [id: string]: MenuModifier[] | undefined }
        | undefined;
};

export type LocationMenuInsightsDataType = {
    itemInsightsDataLoading?: boolean;
    itemInsightsDataFailed?: boolean;
    itemInsightsData: { [storeId: string]: LocationMenuItemRow[] } | undefined;
};

export type CustomerInsightsDataType = {
    customerDataLoading?: boolean;
    customerInsightsDataFailed?: boolean;
    customerData: CustomerTableRow[] | undefined;
};

export type ChannelsDataType = {
    salesChannelDataLoading?: boolean;
    salesChannelDataFailed?: boolean;
    salesChannelReportData: TableChannelDataRow[] | undefined;
};

export type PromotionsReportDataType = {
    promotionsData: PromotionsReportRow[] | undefined;
    promotionsDataLoading?: boolean;
    promotionsDataFailed?: boolean;
};

export type ReportsStateType = {
    dateRanges: Moment[][];
    allTime: boolean;
    comparison: ComparisonType;
    filter: QueryFilter;
    granularity: GranularityType;
    channels: string[];
    stores: IStore[];

    // TODO: Remove us!
    // We shouldn't keep reports data the context state, it would be best held in the useQuery cache.
    // reportsData: ReportsDataType | undefined; // this one has been removed
    locationReportsData: LocationReportsDataType | undefined;
    channelsData: ChannelsDataType | undefined;
    menuInsightsData: MenuInsightsDataType | undefined;
    locationMenuInsightsData: LocationMenuInsightsDataType | undefined;
    customerInsightsData: CustomerInsightsDataType | undefined;
    promotionsReportData: PromotionsReportDataType | undefined;
    giftCardBalanceReportData: GiftCardBalancesDataType | undefined;
    giftCardTransactionReportData: GiftCardTransactionDataType | undefined;
    customHourOffset: string | undefined;
};
